import { Attributes, Validation, ValueObject } from '@singularsystems/neo-core';
import Dividend from '../Dividend';
import DividendAccrualLookup from '../Lookups/DividendAccrualLookup';
import DividendLookup from '../Lookups/DividendLookup';
import ShareTransactionLookup from '../../Transactions/Lookups/ShareTransactionLookup';

export default class EditDividendCommand extends ValueObject {

    constructor(dividend: Dividend) {
        super();
        this.makeObservable();

        this.dividend = dividend;
    }

    @Attributes.ChildObject(Dividend)
    public dividend: Dividend;

    @Attributes.NoTracking()
    public shareTransactions: ShareTransactionLookup[] = [];

    // Client only properties / methods

    @Attributes.NoTracking()
    public accrual: DividendAccrualLookup | null = null;

    @Attributes.NoTracking()
    public isLoading = false;

    @Attributes.NoTracking()
    public isInvestor: boolean = false;

    @Attributes.Float()
    public valuePerShare: number = 0;

    @Attributes.Float()
    public get noOfShares() {
        return this.valuePerShare > 0 ? this.dividend.reinvestAmount / this.valuePerShare : 0;
    }

    public get canEditReinvestAmount() {
        if (this.isInvestor) return false;
        if (!this.dividend.dividendId || (this.dividend.reinvestAmount ?? 0) === 0) return true;
    
        return !this.shareTransactions.some(t => t.transactionDate > this.dividend.payDate!);
    }

    public get canEdit() {
        if (this.accrual) {
            return !this.accrual.isExported && !this.accrual.hasLetter;
        }
        return true;
    }

    public beforeSave(investmentId: number) {
        if (!this.dividend.payDate && this.dividend.ignore) {
            this.dividend.payDate = this.dividend.dividendDate;
        }
        this.dividend.investmentId = investmentId;
    }

    public static fromDividendLookup(dividendLookup: DividendLookup) {
        const editLookup = dividendLookup.clone();

        const dividend = editLookup.dividend ?? Dividend.mapFrom<Dividend>({
            dividendDate: editLookup.calculationDate,
            payDate: editLookup.paidDate,
            dividendAmount: editLookup.calculatedAmount,
        });

        dividend.cycleDate = dividendLookup.cycleDate;

        const command = new EditDividendCommand(dividend);
        command.accrual = dividendLookup.accrual;
        return command;
    }

    public static newDividend(dividendDate: Date) {

        const dividend = Dividend.mapFrom<Dividend>({
            dividendDate: dividendDate,
        });

        return new EditDividendCommand(dividend);
    }

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);
    }

    public toString(): string {
        return "Edit Dividend Command";
    }
}